<template>
    <Headify :data="headMeta" />

    <div
        class="bg-slate-900 select-none"
        style="background-image: url('theme/default/lgbg.jpg')"
    >
        <main class="mx-auto flex min-h-screen w-full items-center justify-center">
            <div
                class="m-4 w-full max-w-md rounded-md border-2 border-slate-800 shadow-xl shadow-slate-600/30"
            >
                <div class="grid grid-cols-2 items-center rounded-t-md bg-slate-900 px-4 py-6">
                    <div>
                        <BrandLogo css-class="w-40" />
                    </div>
                    <div
                        class="flex flex-col gap-y-px text-right text-lg tracking-wider text-gray-200"
                    >
                        <span class="font-medium text-gray-200">
                            <MdiAccount
                                class="mx-auto -mt-1 inline h-5 w-5 text-slate-200"
                                aria-hidden="true"
                            />
                            {{ t('Login') }}
                        </span>
                    </div>
                </div>
                <div class="space-y-6 bg-gray-100 px-4 py-6">
                    <div class="-space-y-px shadow-sm">
                        <div>
                            <label
                                for="email-address"
                                class="sr-only"
                            >
                                {{ t('loginPlaceholder') }}
                            </label>
                            <input
                                id="userName"
                                v-model="userName"
                                type="text"
                                name="userName"
                                autocomplete="off"
                                required=""
                                class="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500 focus:outline-hidden sm:text-sm"
                                :placeholder="t('loginPlaceholder')"
                                @keyup.enter="sendLogin(userName, password)"
                            />
                        </div>
                    </div>

                    <div class="-space-y-px shadow-sm">
                        <div>
                            <label
                                for="email-address"
                                class="sr-only"
                            >
                                {{ t('loginPassword') }}
                            </label>
                            <input
                                id="password"
                                v-model="password"
                                type="password"
                                name="password"
                                autocomplete="off"
                                required=""
                                class="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500 focus:outline-hidden sm:text-sm"
                                :placeholder="t('pwdPlaceholder')"
                                @keyup.enter="sendLogin(userName, password)"
                            />
                        </div>
                    </div>

                    <div>
                        <ActionButton
                            :value="t('buttonLogin')"
                            state="brand"
                            :icon="MdiLock"
                            :is-disabled="loginButtonDisabled()"
                            class-override="w-full block"
                            @click="sendLogin(userName, password)"
                        />
                    </div>
                </div>

                <div class="items-cente flex justify-between rounded-b-md bg-gray-100 px-4 py-4">
                    <a
                        href="#"
                        class="flex-none text-sm font-medium text-sky-600 hover:text-sky-500"
                    >
                        {{ t('cantLogin') }}
                    </a>
                    <div class="flex gap-4 pl-4 text-xs">
                        <div
                            v-tippy="`${serverStatus.beNote} (${copyText.be})`"
                            class="cursor-help rounded-md bg-slate-700 px-3 py-1 font-medium text-gray-300"
                            @click="openServer(copyText.be)"
                        >
                            <MdiServerNetwork
                                :class="[
                                    serverStatus.be !== false ? 'text-emerald-400' : 'text-red-400',
                                    '-mt-1 mr-1 inline h-3 w-3',
                                ]"
                            />
                            BE
                        </div>
                        <div
                            v-tippy="`${serverStatus.fsNote} (${copyText.fs})`"
                            class="cursor-help rounded-md bg-slate-700 px-3 py-1 font-medium text-gray-300"
                            @click="openServer(copyText.fs)"
                        >
                            <MdiServerNetwork
                                :class="[
                                    serverStatus.fs !== false ? 'text-emerald-400' : 'text-red-400',
                                    '-mt-1 mr-1 inline h-3 w-3',
                                ]"
                            />
                            FS
                        </div>
                    </div>
                </div>
                <Spinner
                    v-if="saving.active"
                    type="dots"
                    :color="`text-blue-600`"
                    :color2="`text-blue-400`"
                    :text-color="`text-gray-200`"
                    :overlay="true"
                    :speed="1000"
                    :dots="true"
                    :dot-char="'.'"
                    :dot-dual="false"
                    :dot-reverse="false"
                    :text="saving.text"
                    :inline="false"
                    :pulse="true"
                />
            </div>
        </main>
    </div>
    <div class="fixed bottom-1 left-1 flex h-8 items-end justify-center pb-2 select-text">
        <BuildBadge />
    </div>
</template>

<script setup>
import { ref, reactive, inject } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Headify from '../components/Headify.vue';
import Spinner from '../ui/Spinner.vue';
import navs from '../composables/navs';
import BrandLogo from '../ui/BrandLogo.vue';
import BuildBadge from '../ui/BuildBadge.vue';
import ActionButton from '../ui/ActionButton.vue';
import { login, getLoggedUser } from '../composables/useLogin';
import { pingBackendServer, pingFileStorageServer } from '../composables/useStats';
import { keeper } from '../composables/utils';
import { notify } from 'notiwind';
import { t } from '../composables/i18n';
import { MdiAccount, MdiLock, MdiServerNetwork } from 'materialdesignicons-vue3/icons/';

const config = inject('config');

const userName = ref('');
const password = ref('');
const route = useRoute();
const router = useRouter();
const headMeta = navs().metaFind(route.name);

const saving = reactive({
    active: false,
    text: `${t('loading')}`,
});

const copyText = reactive({
    be: config.env.apiUrl,
    fs: config.env.fileStorageApiUrl,
});

const openServer = (server) => {
    window.open(server);
};

const serverStatus = reactive({
    be: false,
    fs: false,
    beNote: t('serverFail', { serverName: t('backendServerNote') }),
    fsNote: t('serverFail', { serverName: t('fileServerNote') }),
});

const loginButtonDisabled = () => {
    if (config.env.fileStorageEnabled === true) {
        return serverStatus.be === false || serverStatus.fs === false;
    }
    return serverStatus.be === false;
};

const serverCheck = async () => {
    serverStatus.be = await pingBackendServer();
    if (serverStatus.be !== false && serverStatus.be?.status && serverStatus.be?.status === 'UP') {
        serverStatus.beNote = t('serverOk', { serverName: t('backendServerNote') });
    }
    if (config.env.fileStorageEnabled === true) {
        serverStatus.fs = await pingFileStorageServer();
        if (
            serverStatus.fs !== false &&
            serverStatus.fs?.status &&
            serverStatus.fs?.status === 'UP'
        ) {
            serverStatus.fsNote = t('serverFail', {
                serverName: t('fileServerNote'),
            });
        }
    }
};

const sendLogin = async (userName, password) => {
    if (loginButtonDisabled() === false) {
        saving.active = true;
        await login(userName, password, config);
        const loggedUser = getLoggedUser();

        if (loggedUser.role !== 'NONE') {
            saving.active = false;
            notify(
                {
                    group: 'success',
                    title: 'Done',
                    text: `Logged in as ${loggedUser.userName}`,
                },
                2500,
            );
            router.push(keeper('returnUrl') || { name: 'dashboard' });
        } else {
            // Notify problem
            notify(
                {
                    group: 'error',
                    title: 'Error',
                    text: 'Error logging in',
                },
                5000,
            );
            saving.active = false;
        }
    } else {
        notify(
            {
                group: 'error',
                title: 'Error',
                text: 'Error logging in (login disabled)',
            },
            5000,
        );
        saving.active = false;
    }
};

await serverCheck();
</script>
